import { forwardRef, useEffect, useState } from 'react';
import './price.css';
import DirectInput from '../../directInput/DirectInput';
import CharCount from '../../charCount/CharCount';
import useSectionHighlightRef from '../../../hooks/useSectionHighlightRef';
import FixedPrice from '../../price/fixedPrice/FixedPrice';
import CustomPrice from '../../price/customPrice/CustomPrice';

const Price = forwardRef(({ packData, setPackData, cityDrop }, ref) => {
    const { sectionRef } = useSectionHighlightRef(ref);
    const [priceCount, setPriceCount] = useState(1);

    useEffect(() => {
        setPriceCount(packData?.price.length);
    }, [packData?.price.length]);

    function handlePrice(e, index, isVehicleField = false) {
        const { name, value } = e.target;
        if (packData?.datesType === 'custom') {
            setPackData((prevState) => {
                const updatedArray = prevState?.price?.map((item, itemKey) => {
                    if (itemKey !== index) return item;
                    if (name === 'startCity') {
                        return { ...item, [name]: value };
                    }
                    return {
                        ...item,
                        customPrice: {
                            ...item.customPrice,
                            [name]: Math.round(value)
                        }
                    };
                });
                return {
                    ...prevState,
                    price: updatedArray
                };
            });
        } else {
            setPackData((prevState) => {
                const updatedArray = prevState.price.map((item, itemKey) => {
                    if (itemKey !== index) return item;

                    // Update startCity directly
                    if (name === 'startCity') {
                        return { ...item, [name]: value };
                    }

                    let updatedVehicle = item.vehicle?.[0] || { name: '', price: 0 };
                    let updatedPrice = { ...item.price };

                    // ✅ Handle vehicle input
                    if (isVehicleField) {
                        updatedVehicle = {
                            ...updatedVehicle,
                            [name]: name === 'price' ? Math.round(value) : Math.round(value)
                        };
                        // Recalculate total using updated vehicle price
                        const actual = updatedPrice.actual ?? 0;
                        const tax = Math.round((actual * 0.05 * 100) / 100);

                        const total = Math.round(actual + tax);

                        return {
                            ...item,
                            vehicle: [updatedVehicle],
                            price: {
                                ...updatedPrice,
                                tax,
                                total
                            }
                        };
                    }

                    // ✅ Handle normal price input
                    updatedPrice = {
                        ...updatedPrice,
                        [name]: Math.round(value)
                    };

                    const actual = updatedPrice.actual ?? 0;
                    const tax = Math.round((actual * 0.05 * 100) / 100);
                    const total = Math.round(actual + tax);

                    return {
                        ...item,
                        price: {
                            ...updatedPrice,
                            tax,
                            total
                        }
                    };
                });

                return {
                    ...prevState,
                    price: updatedArray
                };
            });
        }
    }

    useEffect(() => {
        if (packData?.datesType === 'custom') {
            setPackData((prevState) => {
                const updatedPriceArray = prevState?.price.map((item) => {
                    const room = Number(item?.customPrice?.room || 0);
                    const perPerson = Number(item?.customPrice?.perPerson || 0);
                    const vehicle = Number(item?.customPrice?.vehicle[0]?.price || 0);

                    const actual = room + perPerson * 2 + vehicle;
                    const tax = Math.round((actual * 0.05 * 100) / 100);
                    const total = Math.round(actual + tax);

                    return {
                        ...item,
                        price: {
                            ...item?.price,
                            actual,
                            tax,
                            total
                        }
                    };
                });

                return {
                    ...prevState,
                    price: updatedPriceArray
                };
            });
        }
    }, [
        packData?.datesType,
        packData?.price?.[0]?.customPrice?.room,
        packData?.price?.[0]?.customPrice?.perPerson,
        packData?.price?.[0]?.customPrice?.vehicle
    ]);

    function addPrice() {
        setPackData(function (prevState) {
            return {
                ...prevState,
                price: [
                    ...prevState.price,
                    {
                        price: {
                            actual: 0,
                            get tax() {
                                return Math.round(this.actual * 0.05);
                            },
                            get total() {
                                return Math.round(this.actual + this.tax + this.vehicle);
                            },
                            reserve: 0
                        },
                        startCity: '',
                        pickupLocation: [],
                        meetupPoint: [],
                        vehicle: []
                    }
                ]
            };
        });
        setPriceCount((prev) => prev + 1);
    }

    function deletePrice() {
        if (priceCount > 1) {
            setPackData(function (prevState) {
                const updatedArrya = [...prevState.price];
                updatedArrya.pop(); //pop should used here only, if use pop in the above line, it returns
                //the removed item which is not the expected outcome
                return {
                    ...prevState,
                    price: updatedArrya
                };
            });
            setPriceCount(priceCount - 1);
        }
    }

    return (
        <div ref={sectionRef}>
            <h2 className='orgPackUpH2'>Price</h2>
            <div className='orgPackUpSecDiv'>
                {packData?.datesType === 'custom' ? (
                    <CustomPrice
                        priceCount={priceCount}
                        packData={packData}
                        cityDrop={cityDrop}
                        handlePrice={handlePrice}
                        setPackData={setPackData}
                    ></CustomPrice>
                ) : (
                    <FixedPrice
                        priceCount={priceCount}
                        packData={packData}
                        cityDrop={cityDrop}
                        handlePrice={handlePrice}
                        setPackData={setPackData}
                    ></FixedPrice>
                )}
                <div className='alignButtons priceAlignButtons'>
                    <button type='button' onClick={addPrice} className='itineraryAddBtn blueBtn'>
                        Add City
                    </button>
                    <button
                        type='button'
                        onClick={deletePrice}
                        className='itineraryDelBtn'
                        disabled={priceCount === 1 ? true : false}
                    >
                        Delete City
                    </button>
                </div>
                {packData?.group === 'vehicle' ? (
                    <></>
                ) : (
                    <DirectInput
                        label='Without Transport Price (optional)'
                        name='withoutTransportPrice'
                        value={packData?.withoutTransportPrice}
                        type='number'
                        placeholder='Without Transport Price'
                        setFormData={setPackData}
                        min={1}
                        limit={6}
                    />
                )}
            </div>
        </div>
    );
});

export default Price;
